import React, { useRef, useState, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { MindMapTreeRoot } from "./MindMapTree";
import { MindMapEdges } from "./MindMapEdges";
import { useNodePositions } from "./hooks/useNodePositions";
import { collectEdges } from "./collectEdges";
import type { MindMapNode, ExpandedMap } from "./types";

interface MindMapCanvasProps {
  root: MindMapNode;
  expanded: ExpandedMap;
  onToggle: (id: string) => void;
  canvasRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
}

export const MindMapCanvas: React.FC<MindMapCanvasProps> = ({
  root,
  expanded,
  onToggle,
  canvasRef,
  className,
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = canvasRef || internalRef;
  const transformRef = useRef<any>(null);
  const positions = useNodePositions(ref, expanded);
  const edges = collectEdges(root, expanded);
  const [hovered, setHovered] = useState(false);

  // Auto-zoom to fit content when expanded state changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (transformRef.current) {
        transformRef.current.zoomToFit(20, 300);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [expanded]);

  return (
    <div
      className={`relative min-h-[280px] w-full overflow-hidden rounded-lg bg-card ${className || ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={0.3}
        maxScale={2}
        wheel={{ step: 0.1 }}
        centerOnInit
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%", minHeight: "280px" }}
          contentStyle={{ width: "fit-content", minWidth: "100%", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "280px" }}
        >
          <div ref={ref} className="relative inline-flex items-center p-12">
            <MindMapEdges edges={edges} positions={positions} />
            <MindMapTreeRoot root={root} expanded={expanded} onToggle={onToggle} />
          </div>
        </TransformComponent>
      </TransformWrapper>
      <div
        className={`pointer-events-none absolute bottom-4 right-4 flex items-center gap-1.5 rounded-lg border border-border bg-card/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm transition-opacity duration-300 ${hovered ? "opacity-100" : "opacity-0"}`}
      >
        <kbd className="inline-block rounded border border-border bg-muted px-1.5 py-0.5 text-[11px]">
          Ctrl
        </kbd>
        <span>+ Scroll&nbsp;&nbsp;or&nbsp;&nbsp;Pinch</span>
      </div>
    </div>
  );
};
